import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ordersAPI } from '../api';
import ProviderMeasurements from './ProviderMeasurements';
import useWebSocket from '../hooks/useWebSocket';
import { 
  ChevronLeft, Package, UserCheck, Scissors, CheckCircle, Truck, Home, Check,
  Zap, Clock, Leaf, Calendar, MapPin, Star, User,
  Share2, Ruler, ChevronDown, RefreshCw, IndianRupee, Shield, XCircle, RotateCcw
} from 'lucide-react';

// Map a backend order document to the fields this screen renders. No demo fallbacks.
export const toTrackingView = (o) => ({
  ...o,
  orderId: o.order_id,
  categoryName: o.product || o.category,
  deliveryType: o.delivery_type,
  deliveryDate: o.delivery_date,
  totalPrice: o.total_price,
  urgentCharge: o.urgent_charge,
  orderDetails: { address: o.address, city: o.city, pincode: o.pincode, paymentMethod: o.payment_method },
  measurements: o.measurements || {},
  tailor: o.tailor_name ? { name: o.tailor_name } : null,
});

const formatGarmentValue = (val) => (typeof val === 'number' ? `${val} cm` : String(val));

const OrderTrackingView = ({ order }) => {
  const navigate = useNavigate();

  const [currentStage, setCurrentStage] = useState(0);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const [showBody, setShowBody] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [rating, setRating] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [ratingError, setRatingError] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelResult, setCancelResult] = useState(null);
  const [cancelError, setCancelError] = useState('');
  // Real-time live update banner
  const [liveUpdate, setLiveUpdate] = useState(null);
  const [liveStatus, setLiveStatus] = useState(order.status || null);

  // Map backend status → stage index
  const statusToStage = (st) => {
    const map = { pending_payment: 0, placed: 0, in_production: 1, assigned: 1, 'counter-offered': 1, stitching: 2, qc_pending: 3, qc_approved: 3, ready: 3, shipped: 4, delivered: 5, completed: 5 };
    return map[st] ?? null;
  };

  // Subscribe to this customer's real-time order updates
  useWebSocket(useCallback((msg) => {
    if (!msg || !msg.type) return;
    // Only react if this message concerns the order we are viewing
    if (msg.order_id && order.orderId && msg.order_id !== order.orderId) return;

    if (msg.type === 'order_update') {
      setLiveStatus(msg.status);
      const stageIdx = statusToStage(msg.status);
      if (stageIdx !== null) setCurrentStage(stageIdx);
      const labels = {
        assigned: 'Tailor Assigned',
        stitching: 'Stitching Started',
        ready: 'Ready for Delivery',
        shipped: 'Out for Delivery',
        delivered: 'Delivered',
        completed: 'Delivered',
        'counter-offered': 'Tailor proposed a new date',
      };
      setLiveUpdate({ label: labels[msg.status] || 'Order Updated', ts: Date.now() });
      setTimeout(() => setLiveUpdate(null), 6000);
    } else if (msg.type === 'cancel_order') {
      setLiveStatus('cancelled');
      setLiveUpdate({ label: 'Order Cancelled', ts: Date.now() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.orderId]));

  const stages = [
    { id: 'placed', label: 'Order Placed', description: liveStatus === 'pending_payment' ? 'Awaiting advance payment' : 'Your order has been confirmed', icon: Package, time: 'Just now' },
    { id: 'assigned', label: 'Tailor Assigned', description: order.tailor?.name || 'Expert tailor matched', icon: UserCheck, time: 'Est. 2 hrs' },
    { id: 'stitching', label: 'Stitching in Progress', description: 'Your garment is being handcrafted', icon: Scissors, time: 'Est. 3-5 days' },
    { id: 'quality', label: 'Quality Check', description: 'Final inspection & finishing', icon: CheckCircle, time: 'Est. 1 day' },
    { id: 'shipped', label: 'Out for Delivery', description: 'On the way to your doorstep', icon: Truck, time: 'Est. 1 day' },
    { id: 'delivered', label: 'Delivered', description: 'Enjoy your custom outfit!', icon: Home, time: '' },
  ];

  // Stage reflects the real backend status (no simulated progress).
  useEffect(() => {
    const idx = statusToStage(liveStatus);
    if (idx !== null) setCurrentStage(idx);
    setShowRating(['completed', 'delivered'].includes(liveStatus) && !order.rating);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveStatus]);

  const getStatus = (i) => i < currentStage ? 'completed' : i === currentStage ? 'active' : 'pending';
  const progress = Math.round(((currentStage + 1) / stages.length) * 100);

  const dlInfo = (() => {
    switch (order.deliveryType) {
      case 'urgent': return { label: 'Urgent', icon: Zap, color: '#991B1B', bg: 'rgba(153,27,27,0.08)' };
      case 'flexible': return { label: 'Flexible', icon: Leaf, color: '#166534', bg: 'rgba(22,101,52,0.08)' };
      default: return { label: 'Standard', icon: Clock, color: '#D4AF37', bg: 'rgba(212,175,55,0.08)' };
    }
  })();
  const DlIcon = dlInfo.icon;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }) : 'TBD';
  const daysLeft = order.deliveryDate ? Math.max(0, Math.ceil((new Date(order.deliveryDate) - new Date()) / 86400000)) : null;

  const measurements = order.measurements || {};
  const tailor = order.tailor;

  return (
    <div data-testid="order-tracking" className="min-h-screen pb-24"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}>

      {/* Header */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-3 py-3 border-b"
        style={{ backgroundColor: 'rgba(250,249,246,0.95)', backdropFilter: 'blur(12px)', borderColor: '#E5E5E5' }}>
        <button data-testid="back-btn" onClick={() => navigate('/landing')} className="p-1 -ml-1">
          <ChevronLeft className="w-5 h-5" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-base" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Track Order</h1>
        <button data-testid="share-btn" className="p-1">
          <Share2 className="w-[18px] h-[18px]" style={{ color: '#595959' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Real-time live update banner */}
      {liveUpdate && (
        <div data-testid="live-update-banner" className="px-3 py-2 flex items-center gap-2 animate-fadeIn"
          style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.12) 0%, rgba(229,178,185,0.12) 100%)', borderBottom: '1px solid rgba(212,175,55,0.3)' }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#166534' }} />
          <span className="text-[11px]" style={{ color: '#0F0F0F' }}>
            Live: <b style={{ color: '#D4AF37' }}>{liveUpdate.label}</b>
          </span>
          <span className="ml-auto text-[9px]" style={{ color: '#595959' }}>Just now</span>
        </div>
      )}

      <div className="px-4 py-4 space-y-3">

        {/* ─── Order Hero Card ─── */}
        <div className="bg-white border p-4" data-testid="order-hero" style={{ borderColor: '#E5E5E5' }}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-[10px] tracking-[0.15em] uppercase" style={{ color: '#595959' }}>Order</p>
              <p className="text-lg font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>{order.orderId}</p>
            </div>
            <span className="flex items-center gap-1 px-2 py-1 text-[10px]" style={{ backgroundColor: dlInfo.bg, color: dlInfo.color }}>
              <DlIcon className="w-3 h-3" strokeWidth={1.5} />{dlInfo.label}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium" style={{ color: '#0F0F0F' }}>{stages[currentStage].label}</span>
              <span className="text-[11px] tabular-nums" style={{ color: '#D4AF37' }}>{progress}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
              <div className="h-full rounded-full transition-all duration-1000 ease-out"
                data-testid="progress-bar"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #E5B2B9, #D4AF37)' }} />
            </div>
          </div>

          {/* Delivery info row */}
          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-[12px]" style={{ color: '#0F0F0F' }}>{formatDate(order.deliveryDate)}</span>
            </div>
            {daysLeft !== null && (
              <span className="text-[11px] px-2 py-0.5" style={{
                backgroundColor: daysLeft <= 2 ? 'rgba(153,27,27,0.08)' : daysLeft <= 5 ? 'rgba(212,175,55,0.08)' : 'rgba(22,101,52,0.08)',
                color: daysLeft <= 2 ? '#991B1B' : daysLeft <= 5 ? '#D4AF37' : '#166534'
              }}>
                {daysLeft === 0 ? 'Arriving today' : `${daysLeft} day${daysLeft > 1 ? 's' : ''} away`}
              </span>
            )}
          </div>
        </div>

        {/* ─── Live Timeline ─── */}
        <div className="bg-white border p-4" data-testid="timeline-section" style={{ borderColor: '#E5E5E5' }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-4" style={{ color: '#595959' }}>Live Status</p>
          <div className="relative">
            {stages.map((stage, i) => {
              const status = getStatus(i);
              const Icon = stage.icon;
              return (
                <div key={stage.id} className="flex gap-3" data-testid={`stage-${stage.id}`}>
                  <div className="flex flex-col items-center">
                    <div className="w-9 h-9 flex items-center justify-center transition-all duration-500"
                      style={{
                        background: status === 'completed' ? '#0F0F0F' : status === 'active' ? 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' : '#FFF',
                        border: status === 'pending' ? '1px solid #E5E5E5' : 'none',
                      }}>
                      {status === 'completed' ? (
                        <Check className="w-4 h-4 text-white" strokeWidth={2} />
                      ) : (
                        <Icon className="w-4 h-4" style={{ color: status === 'active' ? '#FFF' : '#BFBFBF' }} strokeWidth={1.5} />
                      )}
                    </div>
                    {i < stages.length - 1 && (
                      <div className="w-[1.5px] h-10 transition-all duration-500"
                        style={{ backgroundColor: status === 'completed' ? '#0F0F0F' : '#E5E5E5' }} />
                    )}
                  </div>
                  <div className="flex-1 pb-4">
                    <div className="flex items-start justify-between">
                      <p className="text-[12px] transition-colors" style={{ color: status === 'pending' ? '#BFBFBF' : '#0F0F0F', fontWeight: status === 'active' ? 500 : 400 }}>
                        {stage.label}
                      </p>
                      {status !== 'pending' && stage.time && (
                        <span className="text-[10px]" style={{ color: '#BFBFBF' }}>{status === 'completed' ? 'Done' : stage.time}</span>
                      )}
                    </div>
                    <p className="text-[11px]" style={{ color: '#595959' }}>{stage.description}</p>
                    {status === 'active' && (
                      <div className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5" style={{ backgroundColor: 'rgba(212,175,55,0.08)' }}>
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: '#D4AF37' }} />
                        <span className="text-[10px]" style={{ color: '#D4AF37' }}>In progress</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ─── Tailor Card ─── */}
        <div className="bg-white border p-4" data-testid="tailor-card" style={{ borderColor: '#E5E5E5' }}>
          <p className="text-[10px] tracking-[0.12em] uppercase mb-3" style={{ color: '#595959' }}>Your Tailor</p>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
              <User className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <p data-testid="tailor-name" className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>
                {tailor ? tailor.name : 'Tailor not assigned yet'}
              </p>
            </div>
          </div>
        </div>

        {/* ─── Measurements (Collapsible) ─── */}
        <div className="bg-white border" style={{ borderColor: '#E5E5E5' }}>
          <button data-testid="toggle-measurements" onClick={() => setShowMeasurements(!showMeasurements)}
            className="w-full px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Ruler className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-[11px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>Garment Cutting Measurements</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showMeasurements ? 'rotate-180' : ''}`} style={{ color: '#595959' }} strokeWidth={1.5} />
          </button>
          {showMeasurements && (
            <div className="px-4 pb-4 border-t" style={{ borderColor: '#F0F0F0' }}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0 mt-3">
                {Object.entries(measurements).map(([key, val]) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b" style={{ borderColor: '#F5F5F5' }}>
                    <span className="text-[12px] capitalize" style={{ color: '#595959' }}>{key === 'ease' ? 'ease allowance' : key.replace(/_/g, ' ')}</span>
                    <span data-testid={`garment-measure-${key}`} className="text-[12px] font-medium tabular-nums" style={{ color: '#0F0F0F' }}>{formatGarmentValue(val)}</span>
                  </div>
                ))}
              </div>
              {Object.keys(measurements).length === 0 && (
                <p data-testid="garment-measurements-empty" className="text-[11px] mt-3" style={{ color: '#595959' }}>
                  No garment measurements were saved with this order.
                </p>
              )}
              <p className="text-[10px] mt-2" style={{ color: '#8a8a8a' }}>Your body + ease for the chosen fit — what the tailor cuts to.</p>
            </div>
          )}
        </div>

        {/* ─── Body Measurements (3DLOOK) — original provider values, never garment values ─── */}
        <div className="bg-white border" style={{ borderColor: '#E5E5E5' }}>
          <button data-testid="toggle-body-measurements" onClick={() => setShowBody(!showBody)}
            className="w-full px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-[11px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>Body Measurements (3DLOOK)</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showBody ? 'rotate-180' : ''}`} style={{ color: '#595959' }} strokeWidth={1.5} />
          </button>
          {showBody && (
            <div className="border-t" style={{ borderColor: '#F0F0F0' }}>
              {order.provider_measurements?.count > 0
                ? <ProviderMeasurements data={order.provider_measurements} />
                : (
                  <p data-testid="body-measurements-unavailable" className="px-4 py-3 text-[11px]" style={{ color: '#595959' }}>
                    Original 3DLOOK body measurements are not available for this order.
                  </p>
                )}
            </div>
          )}
        </div>

        {/* ─── Order Details (Collapsible) ─── */}
        <div className="bg-white border" style={{ borderColor: '#E5E5E5' }}>
          <button data-testid="toggle-details" onClick={() => setShowDetails(!showDetails)}
            className="w-full px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Package className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <span className="text-[11px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>Order Details</span>
            </div>
            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`} style={{ color: '#595959' }} strokeWidth={1.5} />
          </button>
          {showDetails && (
            <div className="px-4 pb-4 border-t" style={{ borderColor: '#F0F0F0' }}>
              <div className="space-y-2.5 mt-3">
                <div className="flex justify-between text-[12px]">
                  <span style={{ color: '#595959' }}>Product</span>
                  <span style={{ color: '#0F0F0F' }}>{order.categoryName || 'Custom Garment'}</span>
                </div>
                <div className="flex justify-between text-[12px]">
                  <span style={{ color: '#595959' }}>Delivery</span>
                  <span className="flex items-center gap-1" style={{ color: dlInfo.color }}>
                    <DlIcon className="w-3 h-3" strokeWidth={1.5} />{dlInfo.label}
                  </span>
                </div>
                {order.urgentCharge > 0 && (
                  <div className="flex justify-between text-[12px]">
                    <span style={{ color: '#595959' }}>Urgent Charge</span>
                    <span style={{ color: '#991B1B' }}>+{'\u20B9'}{order.urgentCharge}</span>
                  </div>
                )}
                <div className="flex justify-between text-[12px]">
                  <span style={{ color: '#595959' }}>Payment</span>
                  <span className="uppercase text-[11px]" style={{ color: '#0F0F0F' }}>{order.orderDetails?.paymentMethod || 'COD'}</span>
                </div>
                <div className="flex justify-between text-[12px] items-center">
                  <span style={{ color: '#595959' }}>Delivery Address</span>
                  <span className="text-right text-[11px] max-w-[55%]" style={{ color: '#0F0F0F' }}>
                    {order.orderDetails?.address ? `${order.orderDetails.address}, ${order.orderDetails.city}` : 'Not set'}
                  </span>
                </div>
                <div className="pt-2 border-t flex justify-between" style={{ borderColor: '#F0F0F0' }}>
                  <span className="text-[12px] font-medium" style={{ color: '#0F0F0F' }}>Total</span>
                  <span className="text-base font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
                    {'\u20B9'}{(order.totalPrice || 0).toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── Delivery Counter Offer ─── */}
        {currentStage >= 1 && currentStage <= 2 && (
          <div data-testid="delivery-change-card" className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
            <div className="flex items-center gap-2 mb-2">
              <RefreshCw className="w-3.5 h-3.5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
              <p className="text-[11px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>Need a change?</p>
            </div>
            <p className="text-[12px] mb-3" style={{ color: '#595959' }}>
              You can request a different delivery date or contact your tailor directly.
            </p>
            <button data-testid="request-change-btn" onClick={() => navigate('/delivery-negotiation', { state: order })}
              className="w-full py-2 text-[11px] tracking-[0.06em] uppercase flex items-center justify-center gap-1.5 border"
              style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }}>
              <Calendar className="w-3.5 h-3.5" strokeWidth={1.5} /> Request Date Change
            </button>
          </div>
        )}

        {/* ─── Guarantee Badge ─── */}
        <div className="flex items-center gap-2.5 p-3 bg-white border" style={{ borderColor: '#E5E5E5' }}>
          <Shield className="w-5 h-5" style={{ color: '#166534' }} strokeWidth={1.5} />
          <div>
            <p className="text-[12px] font-medium" style={{ color: '#0F0F0F' }}>Lunora's Quality Promise</p>
            <p className="text-[10px]" style={{ color: '#595959' }}>Free alteration within 7 days of delivery</p>
          </div>
        </div>

        {/* ─── Cancel Order ─── */}
        {currentStage < 4 && !cancelResult && (
          <div className="bg-white border p-4" style={{ borderColor: '#E5E5E5' }}>
            {!showCancel ? (
              <button data-testid="cancel-order-btn" onClick={() => setShowCancel(true)}
                className="w-full py-2 text-[11px] tracking-[0.06em] uppercase flex items-center justify-center gap-1.5 border"
                style={{ borderColor: '#E5E5E5', color: '#991B1B' }}>
                <XCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> Cancel Order
              </button>
            ) : (
              <div>
                <p className="text-[12px] mb-2" style={{ color: '#0F0F0F' }}>Cancel this order?</p>
                <p className="text-[10px] mb-3" style={{ color: '#595959' }}>
                  {currentStage <= 1 ? 'Full refund will be processed' : 'Partial refund (50%) based on progress'}
                </p>
                <input data-testid="cancel-reason-input" value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                  placeholder="Reason (optional)" className="w-full py-2 mb-3 text-[12px] bg-transparent border-b focus:outline-none"
                  style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }} />
                <div className="flex gap-2">
                  <button data-testid="confirm-cancel-btn" onClick={async () => {
                    try {
                      const res = await ordersAPI.cancel(order.orderId || order.order_id, cancelReason);
                      setCancelResult(res);
                    } catch (err) { setCancelError(err.message || 'Could not cancel this order'); }
                  }} className="flex-1 py-2 text-[11px] text-white" style={{ backgroundColor: '#991B1B' }}>Confirm Cancel</button>
                  <button onClick={() => setShowCancel(false)} className="px-4 py-2 text-[11px] border" style={{ borderColor: '#E5E5E5', color: '#595959' }}>Keep</button>
                </div>
                {cancelError && <p data-testid="cancel-error" className="text-[11px] mt-2" style={{ color: '#991B1B' }}>{cancelError}</p>}
              </div>
            )}
          </div>
        )}

        {cancelResult && (
          <div data-testid="cancel-result" className="bg-white border p-4 text-center" style={{ borderColor: '#E5E5E5' }}>
            <XCircle className="w-10 h-10 mx-auto mb-2" style={{ color: '#991B1B' }} strokeWidth={1.5} />
            <p className="text-[14px] font-medium mb-1" style={{ color: '#0F0F0F' }}>Order Cancelled</p>
            {cancelResult.refund_amount > 0 && (
              <p className="text-[12px]" style={{ color: '#166534' }}>Refund of {'\u20B9'}{cancelResult.refund_amount} ({cancelResult.refund_percent}%) will be processed</p>
            )}
          </div>
        )}

        {/* ─── Rating (after delivered) ─── */}
        {showRating && !ratingSubmitted && (
          <div data-testid="rating-section" className="bg-white border p-5 text-center animate-fadeIn" style={{ borderColor: '#E5E5E5' }}>
            <div className="w-14 h-14 mx-auto mb-3 flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
              <Check className="w-7 h-7 text-white" strokeWidth={2} />
            </div>
            <p className="text-lg mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Delivered!</p>
            <p className="text-[12px] mb-4" style={{ color: '#595959' }}>How was your experience?</p>
            <div className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map(s => (
                <button key={s} data-testid={`star-${s}`} onClick={() => setRating(s)} className="p-0.5 transition-transform hover:scale-110">
                  <Star className="w-7 h-7 transition-colors" strokeWidth={1.5}
                    style={{ color: s <= rating ? '#D4AF37' : '#E5E5E5', fill: s <= rating ? '#D4AF37' : 'transparent' }} />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <button data-testid="submit-rating-btn" onClick={async () => {
                setRatingError('');
                try {
                  await ordersAPI.update(order.orderId || order.order_id, { rating });
                  setRatingSubmitted(true);
                } catch (err) { setRatingError(err.message || 'Could not submit rating'); }
              }}
                className="px-8 py-2 text-[11px] tracking-[0.08em] uppercase text-white"
                style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
                Submit Review
              </button>
            )}
            {ratingError && <p data-testid="rating-error" className="text-[11px] mt-2" style={{ color: '#991B1B' }}>{ratingError}</p>}
          </div>
        )}

        {ratingSubmitted && (
          <div className="bg-white border p-5 text-center" style={{ borderColor: '#E5E5E5' }}>
            <CheckCircle className="w-10 h-10 mx-auto mb-2" style={{ color: '#166534' }} strokeWidth={1.5} />
            <p className="text-[14px] font-medium" style={{ color: '#0F0F0F' }}>Thank you for your review!</p>
          </div>
        )}

        {/* ─── Reorder ─── */}
        {(currentStage >= 5 || cancelResult) && (
          <button data-testid="reorder-btn" onClick={() => navigate('/gender-selection')}
            className="w-full bg-white border p-4 flex items-center justify-center gap-2 text-[12px]"
            style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }}>
            <RotateCcw className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            Order Again
          </button>
        )}

        {/* Help */}
        <div className="text-center py-2">
          <button data-testid="support-btn" className="text-[12px]" style={{ color: '#D4AF37' }}>
            Need Help? Contact Support
          </button>
        </div>
      </div>

      {/* ─── Sticky Bottom Bar ─── */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 z-40 flex items-center justify-between"
        style={{ borderColor: '#E5E5E5', backdropFilter: 'blur(12px)', backgroundColor: 'rgba(255,255,255,0.95)' }}>
        <div>
          <p className="text-[10px] tracking-[0.1em] uppercase" style={{ color: '#595959' }}>Estimated Delivery</p>
          <p className="text-[14px] font-medium" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>
            {formatDate(order.deliveryDate)}
          </p>
        </div>
        <button data-testid="bottom-my-orders-btn" onClick={() => navigate('/my-orders')}
          className="px-5 py-2 text-[11px] tracking-[0.06em] uppercase text-white flex items-center gap-1.5"
          style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)' }}>
          My Orders
        </button>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </div>
  );
};

// Loads the order from the API (authorised server-side); never renders demo data.
const OrderTracking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.orderId || location.state?.order_id
    || new URLSearchParams(location.search).get('id');
  const [state, setState] = useState({ loading: Boolean(id), order: null });

  useEffect(() => {
    if (!id) return;
    let alive = true;
    ordersAPI.get(id)
      .then(o => alive && setState({ loading: false, order: o?.order_id ? toTrackingView(o) : null }))
      .catch(() => alive && setState({ loading: false, order: null }));
    return () => { alive = false; };
  }, [id]);

  if (state.loading) {
    return <div data-testid="order-tracking-loading" className="min-h-screen flex items-center justify-center text-[12px]" style={{ color: '#595959' }}>Loading your order…</div>;
  }
  if (!state.order) {
    return (
      <div data-testid="order-not-found" className="min-h-screen flex flex-col items-center justify-center px-6 text-center bg-[#FAF9F6]">
        <p className="text-lg mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Order not found</p>
        <p className="text-[12px] mb-5" style={{ color: '#595959' }}>We couldn't find this order on your account.</p>
        <button data-testid="order-not-found-my-orders-btn" onClick={() => navigate('/my-orders')}
          className="px-6 py-3 text-[11px] tracking-[0.18em] uppercase text-white"
          style={{ background: 'linear-gradient(135deg, #1A1A1A 0%, #D4AF37 140%)' }}>
          Go to My Orders
        </button>
      </div>
    );
  }
  return <OrderTrackingView order={state.order} />;
};

export default OrderTracking;
